import Icon from "../icon"
import Input from "../input"
import Select from "../select"
import locale from "../../mixins/locale"
import config from "../../managers/config"

config.defaults({
  "Pagination.pageSize": 20,
  "Pagination.pageSizes": [10, 20, 30, 40, 50, 100],
  "Pagination.layout": "prev,pager,next",
  "Pagination.hideIfSingle": false,
  "Pagination.small": false,
  "Pagination.simple": false,
  "Pagination.pageNumberLimit": 7,
  "Pagination.align": "right",
})

export default {
  name: "mm-pagination",
  mixins: [locale],
  components: {
    "ui-icon": Icon,
    "ui-input": Input,
    "ui-select": Select,
  },
  props: {
    total: {
      type: [Number, String],
      required: true,
    },
    pageSize: {
      type: Number,
      default: () => config.get("Pagination.pageSize"),
    },
    pageSizes: {
      type: Array,
      default: () => config.get("Pagination.pageSizes"),
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    layout: {
      type: String,
      default: () => config.get("Pagination.layout"),
    },
    hideIfSingle: {
      type: Boolean,
      default: () => config.get("Pagination.hideIfSingle"),
    },
    small: {
      type: Boolean,
      default: () => config.get("Pagination.small"),
    },
    simple: {
      type: Boolean,
      default: () => config.get("Pagination.simple"),
    },
    onChange: {
      type: Function,
    },
    pageNumberLimit: {
      type: Number,
      default: () => config.get("Pagination.pageNumberLimit"),
    },
    align: {
      type: String,
      default: () => config.get("Pagination.align"),
      validator(value) {
        return ["left", "right", "center"].includes(value)
      },
    },
  },
  data() {
    return {
      leftJumpIcon: true,
      rightJumpIcon: true,
      localPageSize: 0,
    }
  },
  computed: {
    localTotal() {
      return Number(this.total)
    },
    pageCount() {
      return Math.ceil(this.localTotal / this.localPageSize)
    },
    showPrevJump() {
      return (
        this.pageCount > this.pageNumberLimit &&
        this.currentPage > Math.ceil(this.pageNumberLimit / 2)
      )
    },
    showNextJump() {
      return (
        this.pageCount > this.pageNumberLimit &&
        this.currentPage < this.pageCount - Math.floor(this.pageNumberLimit / 2)
      )
    },
    renderList() {
      const count = this.pageCount
      const page = this.currentPage
      let list = []

      if (count <= this.pageNumberLimit) {
        list = Array.from({ length: count - 1 }).map((_, index) => index + 2)
      }

      if (!this.showPrevJump && this.showNextJump) {
        list = Array.from({ length: this.pageNumberLimit - 1 }).map((_, index) => index + 2)
      }

      if (this.showPrevJump && this.showNextJump) {
        const limit = this.pageNumberLimit - 2 - (this.pageNumberLimit % 2 ^ 1)
        const half = Math.ceil(limit / 2)

        list = Array.from({ length: limit }).map((_, index) => {
          if (index <= half) {
            return page - (half - (index + 1))
          } else {
            return page + index + 1 - half
          }
        })
      }

      if (this.showPrevJump && !this.showNextJump) {
        list = Array.from({ length: this.pageNumberLimit - 1 })
          .map((_, index) => count - index)
          .reverse()
      }

      return list
    },
    pageSizeOptions() {
      return this.pageSizes.map(item => ({
        value: item,
        label: `${item}${this.t("ui.pagination.pageSize")}`,
      }))
    },
    iconSize() {
      return this.small ? "0.625rem" : "0.875rem"
    },
  },
  watch: {
    showPrevJump() {
      this.leftJumpIcon = true
    },
    showNextJump() {
      this.rightJumpIcon = true
    },
    pageSize: {
      immediate: true,
      handler(val) {
        this.localPageSize = val
      },
    },
    localPageSize(val) {
      this.$emit("update:pageSize", val)
      if (this.currentPage !== 1) {
        this.updateCurrentPage(1)
      } else {
        this.onChange && this.onChange(this.currentPage, val)
      }
    },
    pageCount(val) {
      if (val > 0 && this.currentPage > val) {
        this.updateCurrentPage(val)
      }
    },
  },
  methods: {
    getClosetParent(node, tagName) {
      let parent

      while (!parent) {
        if (node.tagName === tagName) {
          parent = node
        } else {
          node = node.parentNode
        }
      }

      return parent
    },
    turnPage(e) {
      let { target } = e
      if (target.tagName === "UL") {
        return
      }

      if (target.tagName !== "LI") {
        target = this.getClosetParent(target, "LI")
      }

      const className = target.className || ""

      if (className.indexOf("size-changer") > -1 || className.indexOf("simple-jumper") > -1) {
        return
      }

      if (className.indexOf("prev") > -1) {
        this.jump(-1)
      } else if (className.indexOf("jump-less") > -1) {
        this.jump(-5)
      } else if (className.indexOf("jump-more") > -1) {
        this.jump(5)
      } else if (className.indexOf("next") > -1) {
        this.jump(1)
      } else {
        this.updateCurrentPage(target.innerText)
      }
    },
    jump(n) {
      let page = this.currentPage + n
      page = page > this.pageCount ? this.pageCount : page < 1 ? 1 : page
      this.updateCurrentPage(page)
    },
    updateCurrentPage(page) {
      page = Number(page)
      if (page !== this.currentPage) {
        this.$emit("update:currentPage", page)
        this.onChange && this.onChange(page, this.localPageSize)
      }
    },
    handleQuickJump(e) {
      let page = Number(e.target.value.trim())

      if (page) {
        page = page > this.pageCount ? this.pageCount : page < 1 ? 1 : page
        this.updateCurrentPage(page)
      }
    },
    getPrev() {
      return (
        <li
          class={{
            disabled: this.currentPage === 1,
            "mm-pagination-prev": true,
          }}
        >
          <ui-icon size={this.iconSize} name="chevronleft" />
        </li>
      )
    },
    getNext() {
      return (
        <li
          class={{
            disabled: this.currentPage === this.pageCount,
            "mm-pagination-next": true,
          }}
        >
          <ui-icon size={this.iconSize} name="chevronright" />
        </li>
      )
    },
    getPager() {
      const { currentPage, showPrevJump, iconSize, renderList, showNextJump, pageCount } = this

      return (
        <li>
          <ul class="mm-pagination-pager">
            <li
              class={{
                active: currentPage === 1,
                "mm-pagination-number": true,
              }}
            >
              <span>1</span>
            </li>
            {showPrevJump && (
              <li
                class="mm-pagination-jump jump-less"
                onMouseenter={() => {
                  this.leftJumpIcon = false
                }}
                onMouseleave={() => {
                  this.leftJumpIcon = true
                }}
              >
                {this.leftJumpIcon ? (
                  <ui-icon size={iconSize} name="threedots" fill="#303133" />
                ) : (
                  <span> ‹‹ </span>
                )}
              </li>
            )}
            {renderList.map(page => (
              <li
                key={page}
                class={{
                  active: currentPage === page,
                  "mm-pagination-number": true,
                }}
              >
                <span>{page}</span>
              </li>
            ))}
            {showNextJump && (
              <li
                class="mm-pagination-jump jump-more"
                onMouseenter={() => {
                  this.rightJumpIcon = false
                }}
                onMouseleave={() => {
                  this.rightJumpIcon = true
                }}
              >
                {this.rightJumpIcon ? (
                  <ui-icon size={iconSize} name="threedots" fill="#303133" />
                ) : (
                  <span> ›› </span>
                )}
              </li>
            )}
            {showNextJump && (
              <li
                class={{
                  active: currentPage === pageCount,
                  "mm-pagination-number": true,
                }}
              >
                <span>{pageCount}</span>
              </li>
            )}
          </ul>
        </li>
      )
    },
    getTotal() {
      return (
        <li class="mm-pagination-total" onClick={e => e.stopPropagation()}>
          {this.t("ui.pagination.total", { total: this.localTotal })}
        </li>
      )
    },
    getSimpleJumper() {
      return (
        <li class="mm-pagination-simple-jumper">
          <ui-input value={this.currentPage} onBlur={this.handleQuickJump} />/ {this.pageCount}
          {this.t("ui.pagination.pageClassifier")}
        </li>
      )
    },
    getJumper() {
      return (
        <li class="mm-pagination-quick-jumper" onClick={e => e.stopPropagation()}>
          {this.t("ui.pagination.goto")}
          &nbsp;
          <ui-input
            size={this.small ? "small" : "medium"}
            value={this.currentPage}
            onBlur={this.handleQuickJump}
          />
          &nbsp;
          {this.t("ui.pagination.pageClassifier")}
        </li>
      )
    },
    getSizes() {
      return (
        <li class="mm-pagination-size-changer">
          <ui-select
            value={this.localPageSize}
            onInput={value => {
              this.localPageSize = value
            }}
            options={this.pageSizeOptions}
            clearable={false}
            size={this.small ? "small" : "medium"}
          />
        </li>
      )
    },
  },
  render(h) { // eslint-disable-line
    const { small, simple, layout, align } = this
    const hide = this.hideIfSingle && (this.pageCount === 1 || this.pageCount < 1)
    const list = (simple ? "simpleJumper,prev,next" : layout).split(",")

    return hide ? null : (
      <ul
        class={{
          "mm-pagination": true,
          "mm-pagination__small": small,
          "mm-pagination__simple": simple,
          [`mm-pagination__${align}`]: align,
        }}
        onClick={this.turnPage}
      >
        {list.map(item => {
          const method = `get${item.replace(/^([a-z])/, s => s.toUpperCase())}`
          return this[method] ? this[method]() : null
        })}
      </ul>
    )
  },
}
